import {
  AddPhotoAlternateOutlined,
  CloseOutlined,
  SaveOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMicrospecDynamicUIContext } from '../../../MicrospecDynamicUI/GlobalProvider';
import {
  getDocById,
  saveDocument,
  updateData,
} from '../../../MicrospecDynamicUI/Helpers/dataUtils';
import uploadSingleFileToStorage from '../../../MicrospecDynamicUI/Helpers/saveFileToStorage';

// ─── Section Wrapper ──────────────────────────────────────────────────────────
const FormSection = ({ title, children }) => (
  <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 3 }}>
    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
      {title}
    </Typography>
    {children}
  </Paper>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const AddCategory = () => {
  const navigate = useNavigate();
  const { categoryId: editId } = useParams();
  const isEdit = Boolean(editId);

  const { cloudProvider } = useMicrospecDynamicUIContext();
  const { firestore, storage } = cloudProvider;

  // ── Form State ────────────────────────────────────────────────────────────
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [order, setOrder] = useState(0);
  const [isPublished, setIsPublished] = useState(true);
  const [existingImage, setExistingImage] = useState('');
  const [newImageFile, setNewImageFile] = useState(null);
  const [newImagePreview, setNewImagePreview] = useState('');
  const fileInputRef = useRef(null);

  // ── UI State ──────────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ── Load existing category (edit mode) ────────────────────────────────────
  useEffect(() => {
    if (!isEdit || !firestore?.instance || !editId) return;

    getDocById(
      (cat) => {
        if (!cat) return;
        setName(cat.name || '');
        setDescription(cat.description || '');
        setOrder(cat.order ?? 0);
        setIsPublished(cat.is_published ?? true);
        setExistingImage(cat.image || '');
      },
      (done) => { if (done) setLoading(false); },
      firestore.instance,
      'categories',
      editId,
      false
    );
  }, [isEdit, firestore?.instance, editId]);

  // ── Image handlers ────────────────────────────────────────────────────────
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (newImagePreview) URL.revokeObjectURL(newImagePreview);
    setNewImageFile(file);
    setNewImagePreview(URL.createObjectURL(file));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const clearImage = () => {
    if (newImagePreview) URL.revokeObjectURL(newImagePreview);
    setNewImageFile(null);
    setNewImagePreview('');
    setExistingImage('');
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = () => {
    if (!name.trim()) return 'يرجى إدخال اسم الفئة';
    return null;
  };

  // ── Upload image ──────────────────────────────────────────────────────────
  const uploadImage = async (categoryId) => {
    if (!newImageFile) return null;
    const path = `categories/${categoryId}/${Date.now()}_${newImageFile.name}`;
    return uploadSingleFileToStorage(storage, path, newImageFile);
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const error = validate();
    if (error) {
      setSnackbar({ open: true, message: error, severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const categoryData = {
        name: name.trim(),
        description: description.trim(),
        order: Number(order) || 0,
        is_published: isPublished,
      };

      if (isEdit) {
        const uploadedUrl = await uploadImage(editId);
        categoryData.image = uploadedUrl || existingImage || '';
        await updateData(`categories/${editId}`, firestore, categoryData);
        setSnackbar({ open: true, message: 'تم تحديث الفئة بنجاح', severity: 'success' });
      } else {
        categoryData.image = '';
        const result = await saveDocument(firestore, 'categories', categoryData);
        if (!result?.success) throw new Error('Failed to save category');

        const docId = result.doc_id;
        const uploadedUrl = await uploadImage(docId);
        if (uploadedUrl) {
          await updateData(`categories/${docId}`, firestore, { image: uploadedUrl });
        }
        setSnackbar({ open: true, message: 'تمت إضافة الفئة بنجاح', severity: 'success' });
      }

      setTimeout(() => navigate('/admin/categories/manage'), 1200);
    } catch (err) {
      console.error('Error saving category:', err);
      setSnackbar({ open: true, message: 'حدث خطأ أثناء الحفظ، يرجى المحاولة مرة أخرى', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const displayImage = newImagePreview || existingImage;

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 3 }}>
        {isEdit ? 'تعديل الفئة' : 'إضافة فئة جديدة'}
      </Typography>

      {/* Basic Info */}
      <FormSection title="المعلومات الأساسية">
        <TextField
          fullWidth
          label="اسم الفئة"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="وصف الفئة"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          rows={3}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="الترتيب"
          type="number"
          value={order}
          onChange={(e) => setOrder(e.target.value)}
          inputProps={{ min: 0 }}
          helperText="الرقم الأصغر يظهر أولاً"
          sx={{ mb: 2 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={isPublished}
              onChange={(e) => setIsPublished(e.target.checked)}
              color="primary"
            />
          }
          label="منشورة"
        />
      </FormSection>

      {/* Image */}
      <FormSection title="صورة الفئة">
        {displayImage ? (
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <Box
              component="img"
              src={displayImage}
              alt="category"
              sx={{
                width: 200,
                height: 120,
                objectFit: 'contain',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                display: 'block',
              }}
            />
            <IconButton
              size="small"
              onClick={clearImage}
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                bgcolor: 'rgba(0,0,0,0.55)',
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                width: 26,
                height: 26,
              }}
            >
              <CloseOutlined sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        ) : (
          <Box
            onClick={() => fileInputRef.current?.click()}
            sx={{
              width: 200,
              height: 120,
              borderRadius: 2,
              border: '2px dashed',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'text.secondary',
              transition: 'border-color 0.2s',
              '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
            }}
          >
            <AddPhotoAlternateOutlined sx={{ fontSize: 32, mb: 0.5 }} />
            <Typography variant="caption">اختر صورة</Typography>
          </Box>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleImageSelect}
        />
        {displayImage && (
          <Button
            size="small"
            startIcon={<AddPhotoAlternateOutlined />}
            onClick={() => fileInputRef.current?.click()}
            sx={{ mt: 1 }}
          >
            تغيير الصورة
          </Button>
        )}
      </FormSection>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/admin/categories/manage')}
          disabled={saving}
        >
          إلغاء
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlined />}
          onClick={handleSubmit}
          disabled={saving}
        >
          {isEdit ? 'حفظ التعديلات' : 'إضافة الفئة'}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ position: 'fixed', bottom: 24 }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddCategory;
